import { initializeFirebase, setupAuth, getUserId, getIsAuthReady, getDb } from './firebase';
import { populateEmployeeSelect, setupUsageListener, renderHolidays, updateUserIdDisplay, showError, hideError, setLogButtonState, clearDateInputs, getFormValues, setupFormHandler, setLoadingIndicator, setHolidayListError, getEmployeeUsage } from './ui';
import { addHoliday, deleteHolidayById, setupHolidayListener, sendHolidayEmail } from './holidayService';
import { calculateDuration } from './utils';
import { MAX_HOLIDAY_DAYS } from './config';
import type { Holiday } from './types';

async function logHoliday(event: Event): Promise<void> {
  event.preventDefault();
  hideError();
  setLogButtonState(true, 'Saving...');

  if (!getIsAuthReady() || !getUserId()) {
    showError('Error: Authentication not ready.');
    setLogButtonState(false);
    return;
  }

  const db = getDb();
  if (!db) {
    showError('Error: Database not initialized.');
    setLogButtonState(false);
    return;
  }

  const formValues = getFormValues();
  if (!formValues) {
    showError('Please select an employee name.');
    setLogButtonState(false);
    return;
  }

  const { name, startDate, endDate } = formValues;

  if (new Date(startDate) > new Date(endDate)) {
    showError('Start Date cannot be after the Return Date.');
    setLogButtonState(false);
    return;
  }

  // Max Holiday Allowance Check
  const duration = calculateDuration(startDate, endDate);
  const newDays = duration.durationDays;
  const currentUsed = getEmployeeUsage(name);
  const totalAfterLogging = currentUsed + newDays;

  if (totalAfterLogging > MAX_HOLIDAY_DAYS) {
    const remaining = MAX_HOLIDAY_DAYS - currentUsed;
    showError(`${name} only has ${remaining} days remaining. This request of ${newDays} days would exceed the ${MAX_HOLIDAY_DAYS}-day limit.`);
    setLogButtonState(false);
    return;
  }

  try {
    // 1. Log to Firestore
    await addHoliday(db, name, startDate, endDate);

    // 2. Call the email simulation
    await sendHolidayEmail(name, startDate, endDate, duration.durationText, getUserId());

    // Clear date inputs after successful submission (keep name selected)
    clearDateInputs();
  } catch (error) {
    console.error("Error adding document or simulating email: ", error);
    showError('Failed to save holiday. Check console for details.');
  } finally {
    setLogButtonState(false);
  }
}

async function deleteHoliday(docId: string): Promise<void> {
  if (!getIsAuthReady() || !getUserId()) {
    console.error("Authentication not ready. Cannot delete holiday.");
    return;
  }

  const db = getDb();
  if (!db) {
    console.error("Database not initialized. Cannot delete holiday.");
    return;
  }

  try {
    await deleteHolidayById(db, docId);
    console.log("Holiday deleted successfully:", docId);
    // onSnapshot listener handles the UI update and usage recalculation automatically.
  } catch (error) {
    console.error("Error deleting document:", docId, error);
  }
}


function setupRealtimeListener(): void {
  if (!getIsAuthReady() || !getUserId()) {
    return; // Wait for auth to be ready
  }

  const db = getDb();
  if (!db) {
    return;
  }

  setLoadingIndicator(true);

  setupHolidayListener(
    db,
    (holidays: Holiday[]) => {
      setLoadingIndicator(false);
      // renderHolidays will calculate and update usage data internally
      renderHolidays(holidays, deleteHoliday);
    },
    (error: Error) => {
      console.error("Error setting up real-time listener:", error);
      setHolidayListError('Error loading data. See console for details.');
    }
  );
}

async function initApp(): Promise<void> {
  try {
    // Initialize Firebase
    initializeFirebase();

    // Populate both employee dropdowns
    populateEmployeeSelect();
    setupUsageListener();

    // Setup authentication
    await setupAuth();
    const userId = getUserId();
    if (userId) {
      updateUserIdDisplay(userId);
    }

    // Attach event listener to the form
    setupFormHandler(logHoliday);

    // Enable button once Firebase is initialized
    setLogButtonState(false);

    // Setup real-time listener
    setupRealtimeListener();

  } catch (error: any) {
    console.error("App initialization failed:", error);
    updateUserIdDisplay('ERROR');
    
    // Show user-friendly error messages for authentication issues
    const errorMessage = error?.message || 'App failed to initialize. Check console.';
    showError(errorMessage);
    setLogButtonState(true);
  }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}

